import {
  Stack,
  Paper,
  rem,
  Title,
  Group,
  Text,
  ActionIcon,
  MultiSelect,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconDotsVertical, IconEdit, IconRefresh, IconUpload } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { contractsApi } from '../../../lib/apis/contracts';
import { ContractsGetDetailsResponse } from '../../../lib/types/contracts';
import { isContractActionClientError } from '../../../lib/utils/contract-action-request';

import { ReportingStatus } from './reporting-status';
import { openImportTelepacModal } from './open-import-telepac-modal';

import { Permission, usePermissions } from '@auth/data-access';
import {
  formatDate,
  FORM_STATUS,
  typedTo,
  useCampaigns,
} from '@data-access';
import { TsrTextLink, TsrNavLink, ActionsMenu, ActionsMenuItem } from '@ui';

interface ContractDetailsSidebarProps {
  contractId: string;
  contract: ContractsGetDetailsResponse;
  selectedCampaignIds: number[];
  onCampaignIdsChange: (campaignIds: number[]) => void;
}

export const Sidebar: FC<ContractDetailsSidebarProps> = ({
  contract,
  contractId,
  selectedCampaignIds,
  onCampaignIdsChange,
}) => {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const queryClient = useQueryClient();
  const campaigns = useCampaigns();
  const canEdit = can(Permission.ManageContracts);

  const showContractActionClientError = (error: unknown) => {
    if (!isContractActionClientError(error)) {
      return;
    }
    notifications.show({
      title: t('common.ERROR'),
      message: error.message,
      color: 'red',
    });
  };

  const rebuildIdentification = useMutation({
    mutationFn: () => contractsApi.rebuildIdentification(Number(contractId)),
    onSuccess: (data) => {
      queryClient.invalidateQueries();
      notifications.show({
        title: t('contracts.rebuild-identification-success'),
        message: data.rebuilt_campaign_years.length
          ? t('contracts.rebuild-identification-success-campaigns', {
              years: data.rebuilt_campaign_years.join(', '),
            })
          : undefined,
        color: 'green',
      });
    },
    onError: showContractActionClientError,
  });

  const startRebuildIdentification = () => {
    modals.closeAll();
    notifications.show({
      title: t('contracts.rebuild-identification-started'),
      message: t('contracts.contract-action-started-body'),
      color: 'blue',
    });
    rebuildIdentification.mutate();
  };

  const handleRebuildIdentification = () => {
    modals.openConfirmModal({
      title: t('contracts.rebuild-identification-confirm-title'),
      children: (
        <Text size="sm" style={{ lineHeight: 1.6 }}>
          {t('contracts.rebuild-identification-confirm-body')}
        </Text>
      ),
      labels: {
        confirm: t('contracts.rebuild-identification'),
        cancel: t('common.cancel'),
      },
      onConfirm: startRebuildIdentification,
    });
  };

  const reassignNgpR26Ilots = useMutation({
    mutationFn: () => contractsApi.reassignNgpR26Ilots(Number(contractId)),
    onSuccess: (data) => {
      queryClient.invalidateQueries();
      notifications.show({
        title: t('contracts.reassign-ilots-ngp-r26-success'),
        message: data.reassigned
          ? undefined
          : t('contracts.reassign-ilots-ngp-r26-success-none'),
        color: 'green',
      });
    },
    onError: showContractActionClientError,
  });

  const startReassignNgpR26Ilots = () => {
    modals.closeAll();
    notifications.show({
      title: t('contracts.reassign-ilots-ngp-r26-started'),
      message: t('contracts.contract-action-started-body'),
      color: 'blue',
    });
    reassignNgpR26Ilots.mutate();
  };

  const handleReassignNgpR26Ilots = () => {
    modals.openConfirmModal({
      title: t('contracts.reassign-ilots-ngp-r26-confirm-title'),
      children: (
        <Text size="sm" style={{ lineHeight: 1.6 }}>
          {t('contracts.reassign-ilots-ngp-r26-confirm-body')}
        </Text>
      ),
      labels: {
        confirm: t('contracts.reassign-ilots-ngp-r26'),
        cancel: t('common.cancel'),
      },
      onConfirm: startReassignNgpR26Ilots,
    });
  };

  const importTelepac = useMutation({
    mutationFn: (file: File) =>
      contractsApi.importTelepac(Number(contractId), file),
    onSuccess: (data) => {
      queryClient.invalidateQueries();
      notifications.show({
        title: t('contracts.import-telepac-success'),
        message: t('contracts.import-telepac-success-detail', {
          year: data.campaign_year,
          count: data.fields_count,
        }),
        color: 'green',
      });
    },
    onError: showContractActionClientError,
  });

  const startImportTelepac = (file: File) => {
    modals.closeAll();
    notifications.show({
      title: t('contracts.import-telepac-started'),
      message: t('contracts.contract-action-started-body'),
      color: 'blue',
    });
    importTelepac.mutate(file);
  };

  const reimportTelepacs = useMutation({
    mutationFn: () => contractsApi.reimportTelepacs(Number(contractId)),
    onSuccess: (data) => {
      queryClient.invalidateQueries();
      const messageParts: string[] = [];
      if (data.reimported_telepac_years.length > 0) {
        messageParts.push(
          t('contracts.reimport-telepacs-success-detail-reimported', {
            years: data.reimported_telepac_years.join(', '),
          }),
        );
      }
      if (data.wiped_stale_telepac_years?.length) {
        messageParts.push(
          t('contracts.reimport-telepacs-success-detail-wiped', {
            years: data.wiped_stale_telepac_years.join(', '),
          }),
        );
      }
      notifications.show({
        title: t('contracts.reimport-telepacs-success'),
        message:
          messageParts.length > 0
            ? messageParts.join(' ')
            : t('contracts.reimport-telepacs-success-detail-none'),
        color: 'green',
      });
    },
    onError: showContractActionClientError,
  });

  const startReimportTelepacs = () => {
    modals.closeAll();
    notifications.show({
      title: t('contracts.reimport-telepacs-started'),
      message: t('contracts.contract-action-started-body'),
      color: 'blue',
    });
    reimportTelepacs.mutate();
  };

  const reimportNgpR26Telepacs = useMutation({
    mutationFn: () => contractsApi.reimportNgpR26Telepacs(Number(contractId)),
    onSuccess: (data) => {
      queryClient.invalidateQueries();
      notifications.show({
        title: t('contracts.reimport-telepacs-ngp-r26-success'),
        message: data.reimported_telepac_years.length
          ? t('contracts.reimport-telepacs-ngp-r26-success-detail')
          : t('contracts.reimport-telepacs-ngp-r26-success-detail-none'),
        color: 'green',
      });
    },
    onError: showContractActionClientError,
  });

  const startReimportNgpR26Telepacs = () => {
    modals.closeAll();
    notifications.show({
      title: t('contracts.reimport-telepacs-ngp-r26-started'),
      message: t('contracts.contract-action-started-body'),
      color: 'blue',
    });
    reimportNgpR26Telepacs.mutate();
  };

  const handleImportTelepac = () => {
    openImportTelepacModal({
      t,
      onImport: startImportTelepac,
    });
  };

  const handleReimportTelepacs = () => {
    modals.openConfirmModal({
      title: t('contracts.reimport-telepacs-confirm-title'),
      children: (
        <Text size="sm" style={{ lineHeight: 1.6 }}>
          {t('contracts.reimport-telepacs-confirm-body')}
        </Text>
      ),
      labels: {
        confirm: t('contracts.reimport-telepacs'),
        cancel: t('common.cancel'),
      },
      confirmProps: { color: 'red' },
      onConfirm: startReimportTelepacs,
    });
  };

  const handleReimportNgpR26Telepacs = () => {
    modals.openConfirmModal({
      title: t('contracts.reimport-telepacs-ngp-r26-confirm-title'),
      children: (
        <Text size="sm" style={{ lineHeight: 1.6 }}>
          {t('contracts.reimport-telepacs-ngp-r26-confirm-body')}
        </Text>
      ),
      labels: {
        confirm: t('contracts.reimport-telepacs-ngp-r26'),
        cancel: t('common.cancel'),
      },
      confirmProps: { color: 'red' },
      onConfirm: startReimportNgpR26Telepacs,
    });
  };

  const actions: ActionsMenuItem[] = [
    {
      label: t('common.edit'),
      icon: IconEdit,
      type: 'link',
      props: {
        disabled: !contract.farm.account || !canEdit,
        to: typedTo('/accounts/$accountId/edit'),
        params: { accountId: contract.farm.account?.id.toString() || '' },
      },
    },
    ...(canEdit
      ? [
          {
            label: t('contracts.import-telepac'),
            icon: IconUpload,
            type: 'action' as const,
            onClick: handleImportTelepac,
          },
          {
            label: t('contracts.rebuild-identification'),
            icon: IconRefresh,
            type: 'action' as const,
            onClick: handleRebuildIdentification,
          },
          ...(contract.clause === 'new'
            ? [
                {
                  label: t('contracts.reassign-ilots-ngp-r26'),
                  icon: IconRefresh,
                  type: 'action' as const,
                  onClick: handleReassignNgpR26Ilots,
                },
              ]
            : []),
          {
            label: t('contracts.reimport-telepacs'),
            icon: IconRefresh,
            type: 'action' as const,
            onClick: handleReimportTelepacs,
            color: 'red' as const,
          },
          ...(contract.clause === 'new'
            ? [
                {
                  label: t('contracts.reimport-telepacs-ngp-r26'),
                  icon: IconRefresh,
                  type: 'action' as const,
                  onClick: handleReimportNgpR26Telepacs,
                  color: 'red' as const,
                },
              ]
            : []),
        ]
      : []),
  ];

  return (
    <Stack>
      <Paper>
        <Group>
          <Stack
            flex="1"
            gap={0}
            h={rem(36)}
            justify="center"
            style={{
              flexWrap: 'nowrap',
              minWidth: 0,
            }}
          >
            <TsrTextLink
              fw="bold"
              lh={1}
              params={{ contractId }}
              size="xl"
              to="/os/contracts/$contractId"
              truncate="end"
            >
              {contract.farm.name}
            </TsrTextLink>
            <TsrTextLink
              lh={1}
              params={{ contractId }}
              size="md"
              to="/os/contracts/$contractId"
              truncate="end"
            >
              {contract.farm.account?.last_name}{' '}
              {contract.farm.account?.first_name}
            </TsrTextLink>
          </Stack>
          <ActionsMenu items={actions}>
            <ActionIcon variant="outline">
              <IconDotsVertical />
            </ActionIcon>
          </ActionsMenu>
        </Group>
      </Paper>

      <Paper>
        <Title order={3} mb="md">
          {t('common.campaign')}
        </Title>
        <MultiSelect
          data={
            campaigns.data?.map((c) => ({
              value: c.id.toString(),
              label: c.name,
            })) || []
          }
          value={selectedCampaignIds.map(String)}
          onChange={(values) =>
            onCampaignIdsChange(values.map((v) => Number(v)))
          }
          placeholder={t('common.campaign')}
          searchable
          clearable
        />
      </Paper>

      <Paper>
        <Title order={3}>{t('common.contractualisation')}</Title>
        <Text>
          {t('common.properties.clause')} : {t(`clauses.${contract.clause}`)}
        </Text>
      </Paper>

      <Paper>
        <Title order={3}>{t('common.declaratif')}</Title>
        <Group align="center" justify="space-between" p="sm" wrap="nowrap">
          <Text>{t('common.consentement-de-lagriculteur')}</Text>
          <ReportingStatus
            status={contract.consent_form?.status || FORM_STATUS.INCOMPLETE}
          />
        </Group>
        <TsrNavLink
          label={
            <Group align="center" wrap="nowrap">
              <Text>{t('common.lots-de-graines-et-parcelles-declarees')}</Text>
              <ReportingStatus status={contract.engaged_fields_status} />
            </Group>
          }
          params={{
            contractId: contractId.toString(),
          }}
          to="/os/contracts/$contractId/batches"
        />
        <TsrNavLink
          disabled
          label={
            <Group
              align="center"
              justify="space-between"
              opacity={0.6}
              wrap="nowrap"
            >
              <Text>{t('common.assolement-recolte-r-n-1')}</Text>
              <ReportingStatus status={FORM_STATUS.INCOMPLETE} />
            </Group>
          }
          params={{
            contractId: contractId.toString(),
          }}
          to="/"
        />
      </Paper>

      <Paper>
        <Text>
          {t('common.parcelles-engagees')} :{' '}
          {contract.engaged_fields_stats.count}
        </Text>
        <Text>
          {t('common.properties.total_ha')} :{' '}
          {contract.engaged_fields_stats.sampling_area.toFixed(2)}{' '}
          {t('common.ha-durables')} (
          {contract.engaged_fields_stats.total_area.toFixed(2)} ha)
        </Text>
      </Paper>
    </Stack>
  );
};
